<template>
    <v-card :loading="loading" min-height="500">
        <template slot="progress">
            <v-overlay absolute class="d-flex flex-column text-center">
                <div>
                    <v-progress-circular size="250" width="10" color="primary" indeterminate>
                        <span>In curs de incarcare...</span>
                    </v-progress-circular>
                </div>
            </v-overlay>
        </template>
        <slot>
            <v-card-title>
                <v-layout row wrap>
                    <v-col cols="6">
                        {{ title }}
                    </v-col>
                    <v-col cols="6" class="text-right">
                        <v-btn
                            v-if="hasAddNewButton"
                            color="primary"
                            dark
                            class="ml-2"
                            @click="addNewItem"
                        >
                            Adaugare
                        </v-btn>
                    </v-col>
                    <v-col md="5" sm="12">
                        <period-selector v-model="period" :button-class="['mt-3']"></period-selector>
                    </v-col>
                    <v-col v-if="hasRole('admin')" md="4" sm="12">
                        <user-selector v-model="filterByUser" filter-by-role="vanzari"></user-selector>
                    </v-col>
                    <v-col md="3" sm="12" class="text-right">
                        <search-field v-model="search"></search-field>
                        <v-checkbox
                            v-if="showTrashedCheckbox"
                            class="mr-5"
                            v-model="showTrashed"
                            label="Afisare informatiile sterse"
                        />
                    </v-col>
                </v-layout>

                <v-dialog v-model="dialogDelete" max-width="500px">
                    <v-card>
                        <v-card-title class="headline">Sunteti sigur ca doriti stergerea intrarii?</v-card-title>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="red darken-1" text @click="closeDelete">Anulare</v-btn>
                            <v-btn color="blue darken-1" text @click="deleteItemConfirm">Confirmare</v-btn>
                            <v-spacer></v-spacer>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-dialog v-model="dialogForceDelete" overlay-color="red" max-width="500px">
                    <v-card>
                        <v-card-title class="headline">Sunteti sigur ca doriti stergerea intrarii DEFINITIV?</v-card-title>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="red darken-1" text @click="closeForceDelete">Anulare</v-btn>
                            <v-btn color="blue darken-1" text @click="forceDeleteItemConfirm">Confirmare</v-btn>
                            <v-spacer></v-spacer>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-card-title>
        </slot>
        <v-data-table
            v-model="selected"
            :headers="headers"
            :items="items"
            :options.sync="options"
            :server-items-length="meta.pagination.total"
            class="elevation-1"
            show-select
        >
            <template v-slot:top>
                <v-toolbar
                    flat
                >
                    <v-toolbar-title>{{ title }}</v-toolbar-title>
                    <v-divider
                        class="mx-4"
                        inset
                        vertical
                    ></v-divider>
                    <v-spacer></v-spacer>
                    <v-checkbox
                        v-if="hasRole('admin')"
                        class="mr-5 mt-3"
                        v-model="exportOptions.includeEntryPrice"
                        label="Afisare cost + furnizor"
                    />
                    <v-btn
                        v-if="selected.length > 1"
                        color="primary"
                        dark
                        class="mb-2"
                        @click="downloadSelected"
                    >
                        Descarcare elemente selectate
                    </v-btn>
                    <v-btn
                        color="primary"
                        dark
                        class="ml-2"
                        @click="createExport"
                    >
                        Export PDF
                    </v-btn>
                </v-toolbar>
            </template>
            <template v-slot:item.created_by="{ item }">
                {{ item.created_by.name }}
            </template>
            <template v-slot:item.type="{ item }">
                {{ item.type_friendly }}
            </template>
            <template v-slot:item.invoice_no="{ item }">
                {{ item.invoice_number }}
            </template>
            <template v-slot:item.created_at="{ item }">
                {{ new Date(item.created_at) | date }}
            </template>
            <template v-slot:item.customer_id="{ item }">
                {{ _.get(item, 'customer.name') }} {{ _.get(item, 'customer.surname') }}
            </template>
            <template v-slot:item.amount_with_vat="{ item }">
                {{ item.amount_with_vat | price }}
            </template>
            <template v-slot:item.actions="{ item }">
                <v-btn
                    v-if="item.subject_has_worksheet"
                    color="primary"
                    small
                    class="mr-2"
                    outlined
                    rounded
                    @click="downloadInvoiceWorksheet(item)"
                >
                    Deviz
                </v-btn>
                <v-btn
                    v-if="!_.isNil(item.recipe)"
                    color="primary"
                    small
                    class="mr-2"
                    outlined
                    rounded
                    @click="downloadInvoiceRecipe(item)"
                >
                    Ch.
                </v-btn>
                <v-btn
                    color="primary"
                    x-small
                    class="mr-2"
                    outlined
                    rounded
                    fab
                    @click="downloadInvoice(item)"
                >
                    <v-icon small>
                        mdi-file-pdf
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="item.type === 'INVOICE'"
                    color="warning"
                    small
                    class="mr-2"
                    outlined
                    rounded
                    @click="deleteItem(item)"
                >
                    <v-icon small>
                        mdi-delete
                    </v-icon>
                    Anulare
                </v-btn>
                <v-btn
                    v-if="hasRole('admin')"
                    color="error"
                    small
                    class="mr-2"
                    outlined
                    rounded
                    @click="forceDeleteItem(item)"
                >
                    <v-icon small>
                        mdi-alert-circle
                    </v-icon>
                    Stergere
                </v-btn>
            </template>
            <template v-slot:no-data>
                <p class="my-2">Nu am gasit nici un rezultat.</p>
                <v-btn
                    class="my-2"
                    color="primary"
                    @click="reset"
                >
                    Eliminare filtre
                </v-btn>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
    import SmartTable from "Components/SmartTable";
    import PeriodSelector from "Components/common/PeriodSelector";
    import UserSelector from "Components/common/UserSelector";
    import ACL from "Mixins/acl";
    import {Form} from "vform";
    export default {
        mixins: [ACL],
        components: {
            PeriodSelector,
            UserSelector
        },
        extends: SmartTable,
        data: () => ({
            title: 'Lista facturi emise',
            loading: false,
            dialog: false,
            dialogDelete: false,
            dialogForceDelete: false,
            selected: [],
            headers: [
                { text: 'Tip', value: 'type' },
                { text: 'Numar factura', value: 'invoice_no' },
                { text: 'Data', value: 'created_at' },
                { text: 'Valoare', value: 'amount_with_vat' },
                { text: 'Client', value: 'customer_id' },
                { text: 'Emis de', value: 'created_by' },
                { text: 'Actiuni', value: 'actions' }
            ],
            options: {
                sortBy: ['id'],
                sortDesc: [true]
            },
            items: [],
            editedIndex: -1,
            showTrashedCheckbox: true,
            showTrashed: false,
            period: [window.moment().subtract(1, 'month').toISOString(), window.moment().toISOString()],
            filterByUser: null,
            exportOptions: {
                includeEntryPrice: false
            }
        }),

        computed: {
            formTitle () {
                return this.editedIndex === -1 ? 'Adaugare' : 'Modificare'
            },
        },

        watch: {
            showTrashed: {
                handler(val) {
                    this.initialize()
                }
            },
            period: {
                async handler(val) {
                    await this.initialize()
                }
            },
            filterByUser: {
                async handler(val) {
                    await this.initialize()
                }
            },
            dialogForceDelete (val) {
                val || this.closeForceDelete()
            },
        },

        async created () {
            await this.initialize()
            let downloadInvoiceId = this.$route.query?.downloadInvoice || 0
            if (parseInt(downloadInvoiceId) > 0) {
                this.downloadInvoice({id: downloadInvoiceId})
            }
            let downloadEowId = this.$route.query?.downloadEow || 0
            if (parseInt(downloadEowId) > 0) {
                this.downloadInvoiceWorksheet({subject_id: downloadEowId, subject_type: 'App\\Models\\Offer\\Offer'})
            }
        },

        methods: {
            async initialize () {
                this.loading = true
                var query = this.getQuery()
                let { data, meta } = await query.get()
                this.items = data
                this.meta = meta
                this.selected = []
                this.loading = false
            },

            close () {
                this.dialog = false
                this.$nextTick(() => {
                    this.editedItem = Object.assign({}, this.defaultItem)
                    this.editedIndex = -1
                })
            },

            async downloadInvoice(item) {
                const downloadEndpoint = new this.$db.Invoice({id: item.id}).downloadEndpoint
                this.loading = true
                return window.axios.get(`${downloadEndpoint}`, {
                    responseType: 'blob',
                    params: this.exportOptions
                }).then((response) => {
                    const contentDisposition = this._.get(response, 'headers.content-disposition')
                    const filename = contentDisposition.split("filename=")[1];
                    const downloadUrl = window.URL.createObjectURL(new Blob([response?.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', filename); //any other extension
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            },

            async downloadInvoiceRecipe(item) {
                const downloadEndpoint = new this.$db.Invoice({id: item.id}).downloadRecipeEndpoint
                this.loading = true
                return window.axios.get(`${downloadEndpoint}`, {
                    responseType: 'blob',
                    params: this.exportOptions
                }).then((response) => {
                    const contentDisposition = this._.get(response, 'headers.content-disposition')
                    const filename = contentDisposition.split("filename=")[1];
                    const downloadUrl = window.URL.createObjectURL(new Blob([response?.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', filename); //any other extension
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            },

            async downloadInvoiceWorksheet(item) {
                let downloadEndpoint = undefined
                switch (item.subject_type) {
                    case 'App\\Models\\Offer\\Offer':
                        downloadEndpoint = new this.$db.Offer({id: item.subject_id}).downloadEstimateOfWork
                        break
                    default:
                        alert('Neimplementat!')
                        return
                }
                this.loading = true
                return window.axios.get(`${downloadEndpoint}`, {
                    responseType: 'blob',
                    params: this.exportOptions
                }).then((response) => {
                    const contentDisposition = this._.get(response, 'headers.content-disposition')
                    const filename = contentDisposition.split("filename=")[1];
                    const downloadUrl = window.URL.createObjectURL(new Blob([response?.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', filename); //any other extension
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            },
            getQuery(extraParams) {
                var query = this.$db.Invoice
                    .append('invoice_number', 'subject_has_worksheet')
                    .include('recipe', 'customer', 'createdBy')
                    .orderBy(this.orderBy)
                    .page(this.options.page)
                    .limit(this.options.itemsPerPage)
                    .params(Object.assign({
                        query: this.search
                    }, extraParams))
                if(this.period instanceof Array && this.period.length === 2) {
                    query = query.where('between', [window.moment(this.period[0]).format('YYYY-MM-DD'), window.moment(this.period[1]).format('YYYY-MM-DD')].join(','))
                }
                if(this.showTrashed) query = query.where('onlyTrashed', true)
                if(!this._.isNil(this.filterByUser)) query = query.where('created_by', this.filterByUser.join(','))
                return query
            },
            async createExport() {
                this.loading = true
                let query = this.getQuery({export: true})
                let base = query._fromResource || `${query.baseURL()}/${query.resource()}`
                base = query._customResource
                    ? `${query.baseURL()}/${query._customResource}`
                    : base
                let url = `${base}${query._builder.query()}`
                return window.axios.get(`${url}`, {
                    responseType: 'blob',
                    params: this.exportOptions
                }).then((response) => {
                    const contentDisposition = this._.get(response, 'headers.content-disposition')
                    const filename = contentDisposition.split("filename=")[1];
                    const downloadUrl = window.URL.createObjectURL(new Blob([response?.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', filename); //any other extension
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }).catch((err) => {
                    console.error(err)
                }).finally(() => {
                    this.loading = false
                })
            },

            deleteItem (item) {
                this.editedIndex = this.items.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.dialogDelete = true
            },

            async deleteItemConfirm () {
                let item = this.items[this.editedIndex]
                if(item instanceof this.$db.Invoice) {
                    await item.delete().catch((error) => {
                        if(error?.response?.status === 403) {
                            this.showError(error?.response?.data?.error)
                        } else {
                            this.showError()
                        }
                    })
                } else {
                    console.warn('Here comes the headache...')
                }
                this.dialogDelete = false
                await this.initialize()
            },

            forceDeleteItem (item) {
                this.editedIndex = this.items.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.dialogForceDelete = true
            },

            async forceDeleteItemConfirm () {
                let item = this.items[this.editedIndex]
                if(item instanceof this.$db.Invoice) {
                    await item.config({ headers: { 'X-Force': true } }).delete().catch((error) => {
                        if(error?.response?.status === 403) {
                            this.showError(error?.response?.data?.error)
                        } else {
                            this.showError()
                        }
                    })
                } else {
                    console.warn('Here comes the headache...')
                }
                this.dialogForceDelete = false
                await this.initialize()
            },

            closeForceDelete () {
                this.dialogForceDelete = false
                this.$nextTick(() => {
                    this.form = new Form(Object.assign({}, this.defaultItem))
                    this.editedIndex = -1
                })
            },
            async downloadSelected() {
                this.loading = true
                this.asyncForEach(this.selected, async(invoice) => {
                    await this.downloadFile(invoice.downloadEndpoint, this.exportOptions)
                        .catch((e) => {
                            this.showError(e.toString())
                        })
                }).finally(() => {
                    this.loading = false
                })
            }
        },
    };
</script>
